/**
 * DateChangeRequestButton — F-S2.
 *
 * Renders a project date plus an edit affordance whose behaviour depends on the current user's role:
 *  - leadership / admin → inline date picker (Popover) that PATCHes /api/projects/{id} directly,
 *    no approval needed.
 *  - pm / sa            → opens the RequestDateChangeModal (leadership approval workflow).
 *  - any other role     → read-only (date shown, no edit control).
 *
 * A yellow "Pending" badge is shown when an unresolved request already exists for this field.
 */
import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Pencil } from "lucide-react";

import type { DateChangeField } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useUpdateProject } from "@/hooks/useProjects";
import { formatDateValue, toDateInputValue } from "@/lib/date-change";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RequestDateChangeModal } from "@/components/projects/RequestDateChangeModal";

export interface DateChangeRequestButtonProps {
  projectId: string;
  dateField: DateChangeField;
  currentValue: string | null;
  label: string;
  /** Whether an unresolved request already exists for this field. */
  hasPending?: boolean;
  /** Called after a request is submitted or a direct edit is saved. */
  onRequested: () => void;
}

/** Inline date editor for leadership/admin — saves via PATCH /api/projects/{id}. */
function InlineDateEditor({
  projectId,
  dateField,
  currentValue,
  label,
  onSaved,
}: {
  projectId: string;
  dateField: DateChangeField;
  currentValue: string | null;
  label: string;
  onSaved: () => void;
}): JSX.Element {
  const updateProject = useUpdateProject(projectId);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(toDateInputValue(currentValue));

  const handleSave = (): void => {
    if (!value) {
      toast.error("Please choose a date.");
      return;
    }
    updateProject.mutate(
      { [dateField]: value },
      {
        onSuccess: () => {
          toast.success(`${label} updated`);
          onSaved();
          setOpen(false);
        },
        onError: (err: unknown) => {
          const msg =
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { data?: { message?: string } } }).response
                  ?.data?.message
              : undefined;
          toast.error(msg || `Failed to update ${label.toLowerCase()}`);
        },
      },
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setValue(toDateInputValue(currentValue));
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-text-secondary hover:text-text-primary"
          aria-label={`Edit ${label}`}
          title={`Edit ${label}`}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={`inline-date-${dateField}`}>{label}</Label>
          <DatePicker
            id={`inline-date-${dateField}`}
            value={value}
            onChange={setValue}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={updateProject.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateProject.isPending || !value}
          >
            {updateProject.isPending && (
              <Loader2
                className="h-3.5 w-3.5 animate-spin"
                aria-hidden="true"
              />
            )}
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DateChangeRequestButton({
  projectId,
  dateField,
  currentValue,
  label,
  hasPending = false,
  onRequested,
}: DateChangeRequestButtonProps): JSX.Element {
  const { user } = useAuth();
  const role = user?.role;
  const canDirectEdit = role === "leadership" || role === "admin";
  const canRequest = role === "pm" || role === "sa";

  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-text-primary">
        {formatDateValue(currentValue)}
      </span>

      {canDirectEdit && (
        <InlineDateEditor
          projectId={projectId}
          dateField={dateField}
          currentValue={currentValue}
          label={label}
          onSaved={onRequested}
        />
      )}

      {canRequest && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-text-secondary hover:text-text-primary"
          aria-label={`Request change to ${label}`}
          title={`Request ${label} change`}
          onClick={() => setModalOpen(true)}
        >
          <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}

      {hasPending && (
        <Badge variant="warning" className="gap-1">
          <CalendarClock className="h-3 w-3" aria-hidden="true" />
          Pending
        </Badge>
      )}

      {canRequest && (
        <RequestDateChangeModal
          open={modalOpen}
          projectId={projectId}
          dateField={dateField}
          currentValue={currentValue}
          label={label}
          onSubmitted={onRequested}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

export default DateChangeRequestButton;
